<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import ConfirmPopup from "primevue/confirmpopup";
import Toast from "primevue/toast";
import { useLayout } from "src/composables/useLayout";
import { useUserStore } from "src/stores/user-store";

import AppFooter from "./AppFooter.vue";
import AppSidebar from "./AppSidebar.vue";
import AppTopbar from "./AppTopbar.vue";
import { RouteName } from "src/enums/route-name";

const userStore = useUserStore();
const { layoutConfig, layoutState, isSidebarActive, resetMenu } = useLayout();
const router = useRouter();

const { config } = storeToRefs(userStore);
const onShortcutUpdateRefreshKey = ref(0);

const outsideClickListener = ref(null);

const routeName = computed(() => router.currentRoute.value.name);

const containerClass = computed(() => {
  return {
    "layout-overlay": layoutConfig.value.menuMode === "overlay",
    "layout-static": layoutConfig.value.menuMode === "static",
    "layout-static-inactive": layoutState.value.staticMenuDesktopInactive && layoutConfig.value.menuMode === "static",
    "layout-overlay-active": layoutState.value.overlayMenuActive,
    "layout-mobile-active": layoutState.value.staticMenuMobileActive,
  };
});

const isLayoutFullWidth = computed(() => {
  const fullWidthRoutes = [RouteName.TICKET_BOARD];
  return fullWidthRoutes.includes(routeName.value as RouteName);
});

function bindOutsideClickListener() {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        resetMenu();
      }
    };
    document.addEventListener("click", outsideClickListener.value);
  }
}

function unbindOutsideClickListener() {
  if (outsideClickListener.value) {
    document.removeEventListener("click", outsideClickListener.value);
    outsideClickListener.value = null;
  }
}

function isOutsideClicked(event) {
  const sidebarEl = document.querySelector(".layout-sidebar");
  const topbarEl = document.querySelector(".layout-menu-button");

  return !(
    sidebarEl.isSameNode(event.target) ||
    sidebarEl.contains(event.target) ||
    topbarEl.isSameNode(event.target) ||
    topbarEl.contains(event.target)
  );
}

watch(isSidebarActive, (newVal) => {
  if (newVal) {
    bindOutsideClickListener();
  } else {
    unbindOutsideClickListener();
  }
});

// Add onBeforeUnmount hook to clean up event listeners
onBeforeUnmount(() => {
  unbindOutsideClickListener();
});

</script>

<template>
  <div  class="layout-wrapper layout-static" :class="containerClass">
    <app-topbar />
    <app-sidebar />
    <div class="layout-main-container">
      <div class="layout-main" :class="{ 'full-width': isLayoutFullWidth }">
        <router-view :key="routeName" />
      </div>
    </div>
    <div class="layout-mask animate-fadein" @click="resetMenu" />
    <ConfirmPopup />
    <Toast position="top-center" />
  </div>
</template>

<style lang="scss" scoped></style>
